import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '@/services/api'
import type { CreateReservationPayload } from '@/lib/schemas/create-reservation'

export type { CreateReservationPayload } from '@/lib/schemas/create-reservation'

/** El que fa servir el frontend de la reserva que torna `POST /reservations` (`ReservationResponseDto`). */
export interface CreatedReservation {
  id: string
  /** Número de Beds24; `manual-…` si la propietat no sincronitza. */
  beds24_id?: string
  /** La reserva és creada, però falta alguna cosa (p. ex. el client de facturació a la fitxa). */
  warnings?: string[]
}

/** Tot el que ha de tornar a demanar-se després de crear: llistes, portada i la graella del calendari. */
export const CREATE_RESERVATION_INVALIDATE_KEYS = [
  ['reservations'],
  ['dashboard-stats'],
  ['upcoming-reservations'],
  ['calendar-bookings'],
] as const

export function invalidateAfterCreateReservation(queryClient: Pick<QueryClient, 'invalidateQueries'>): void {
  for (const queryKey of CREATE_RESERVATION_INVALIDATE_KEYS) {
    void queryClient.invalidateQueries({ queryKey: [...queryKey] })
  }
}

/**
 * Avisos d'una creació que **ha anat bé** però deixa alguna cosa pendent: el `warnings` de
 * `POST /reservations`, `/reservations/group` i `/reservations/:id/stays` (p. ex. el client de
 * facturació no s'ha pogut desar al metadata). Llegit de forma defensiva; només textos no buits.
 */
export function readCreationWarnings(data: unknown): string[] {
  if (typeof data !== 'object' || data === null) return []
  const warnings = (data as { warnings?: unknown }).warnings
  if (!Array.isArray(warnings)) return []
  return warnings
    .filter((warning): warning is string => typeof warning === 'string' && warning.trim() !== '')
    .map((warning) => warning.trim())
}

/**
 * El toast d'una creació que ha anat bé: el d'èxit o, si el backend porta avisos, un toast d'avís per
 * a cadascun (la reserva ja és creada igualment: el text diu què falta i que no es torni a crear).
 */
export function toastCreationSuccess(successMessage: string, warnings: string[]): void {
  if (warnings.length === 0) {
    toast.success(successMessage)
    return
  }
  for (const warning of warnings) toast.warning(warning, { duration: 15000 })
}

/** Missatge del backend (text o llista de class-validator) o, si no n'hi ha, un de genèric. */
export function createReservationErrorMessage(error: unknown): string {
  const fallback = 'Error en crear la reserva'
  if (typeof error !== 'object' || error === null) return fallback
  const response = (error as { response?: { data?: unknown } }).response
  const data = response?.data
  if (typeof data === 'object' && data !== null && 'message' in data) {
    const message = (data as { message?: unknown }).message
    if (typeof message === 'string' && message.trim() !== '') return message
    if (Array.isArray(message) && message.length > 0) {
      return message.filter((m): m is string => typeof m === 'string').join(' · ') || fallback
    }
  }
  if (error instanceof Error && error.message.trim() !== '') return error.message
  return fallback
}

export function useCreateReservation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: CreateReservationPayload): Promise<CreatedReservation> => {
      const response = await api.post<CreatedReservation>('/reservations', payload)
      return response.data
    },
    onSuccess: (created) => {
      invalidateAfterCreateReservation(queryClient)
      toastCreationSuccess('Reserva creada correctament', readCreationWarnings(created))
    },
    onError: (error: unknown) => {
      toast.error(createReservationErrorMessage(error))
    },
  })
}
